import React, { useState, useEffect } from 'react';
import axiosInstance from '../utills/axiosInstance';

const styles = {
  container: { padding: '2rem', fontFamily: "'Segoe UI', sans-serif", backgroundColor: '#f4f6f8', minHeight: '100vh' },
  title: { margin: 0, fontSize: '2rem', color: '#333' },
  section: { backgroundColor: '#fff', borderRadius: '10px', padding: '1.5rem', marginBottom: '2rem', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
  sectionTitle: { marginBottom: '1rem', color: '#2c3e50' },
  form: { display: 'flex', flexDirection: 'column', gap: '0.8rem' },
  input: { padding: '0.5rem 1rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '1rem' },
  select: { padding: '0.5rem 1rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '1rem' },
  button: { backgroundColor: '#2e86de', color: 'white', border: 'none', borderRadius: '5px', padding: '0.6rem 1.2rem', cursor: 'pointer', fontSize: '1rem' },
  editButton: { backgroundColor: '#f39c12', color: 'white', border: 'none', borderRadius: '4px', padding: '0.4rem 0.8rem', cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: '1rem' },
  tableHeader: { backgroundColor: '#2e86de', color: '#fff', textAlign: 'left', padding: '0.8rem' },
  tableRow: { borderBottom: '1px solid #ddd' },
};

const modalStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: '12px', padding: '2rem', width: '90%', maxWidth: '560px', boxShadow: '0 8px 20px rgba(0,0,0,0.15)', maxHeight: '90vh', overflowY: 'auto' },
  title: { marginTop: 0, marginBottom: '1.2rem', color: '#2c3e50' },
};

const FilePreview = ({ file, customerId, onFileDeleted }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const fileUrl = `${backendUrl}/api/customers/file/${file.fileId}`;
  const isImage = /\.(jpe?g|png|gif|bmp|webp)$/i.test(file.filename);

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${file.filename}"?`)) return;
    try {
      await axiosInstance.delete(`/api/customers/file/${customerId}/${file.fileId}`);
      onFileDeleted();
    } catch {
      alert('Delete failed');
    }
  };

  return (
    <div style={{ marginBottom: '6px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
      {isImage ? (
        <a href={fileUrl} target="_blank" rel="noopener noreferrer">
          <img src={fileUrl} alt="" style={{ maxHeight: '40px', maxWidth: '60px', borderRadius: '4px' }} />
        </a>
      ) : (
        <a href={fileUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2e86de' }}>
          {file.filename}
        </a>
      )}
      <span style={{ color: '#777', fontSize: '0.75rem' }}>({ (file.size / 1024).toFixed(1) } KB)</span>
      <button onClick={handleDelete} style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: '3px', padding: '2px 6px', fontSize: '0.7rem', cursor: 'pointer' }}>X</button>
    </div>
  );
};

/* ---------- ENUMS (exact match with backend) ---------- */
const INSPECTION_OPTIONS = ['Pending', 'Completed', 'Rescheduled', 'In Progress', 'Door Closed', 'Address Incorrect', 'Contact Number Incorrect', 'Person Missing'];
const KYC_OPTIONS = ['Pending', 'Completed', 'In Progress', 'Person Missing', 'Address Incorrect', 'Door Closed', 'Contact Number Incorrect'];

export default function Customer() {
  const [customers, setCustomers] = useState([]);
  const [newCustomer, setNewCustomer] = useState({ name: '', mobile: '', address: '', inspection: 'Pending', completionDate: '', kycStatus: 'Pending', files: [] });
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importCsvFile, setImportCsvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedImportType, setSelectedImportType] = useState('');

  const email = localStorage.getItem('userEmail');

  /* ---------- FILE HELPERS ---------- */
  const uploadFiles = async (customerId, files) => {
    if (!files?.length) return;
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    fd.append('uploaderEmail', email);
    await axiosInstance.post(`/api/customers/upload/${customerId}`, fd);
  };

  const addNewFiles = (e, setCustomer) => {
    const files = Array.from(e.target.files);
    setCustomer(prev => ({ ...prev, files: [...prev.files.filter(f => !(f instanceof File)), ...files] }));
    e.target.value = ''; // reset input
  };

  const removeNewFile = (index, setCustomer) => {
    setCustomer(prev => ({ ...prev, files: prev.files.filter((_, i) => i !== index) }));
  };
  

  /* ---------- API ---------- */
  const fetchCustomers = async () => {
    setLoading(true);
    try {
      if (!email) return alert('Login required');
      const res = await axiosInstance.get(`/api/customers?email=${encodeURIComponent(email)}`);
      setCustomers(res.data);
    } catch {
      alert('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCustomers(); }, []);

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    if (!newCustomer.name?.trim() || !newCustomer.mobile?.trim()) return alert('Name & Mobile required');
    if (!/^[6-9]\d{9}$/.test(newCustomer.mobile)) return alert('Valid 10-digit mobile required');

    try {
      const payload = {
        ...newCustomer,
        uploaderEmail: email,
        completionDate: newCustomer.inspection === 'Completed' ? newCustomer.completionDate : undefined,
      };
      const res = await axiosInstance.post('/api/customers', payload);
      const newFiles = newCustomer.files.filter(f => f instanceof File);
      if (newFiles.length) await uploadFiles(res.data._id, newFiles);
      await fetchCustomers();
      setShowCreateModal(false);
      setNewCustomer({ name: '', mobile: '', address: '', inspection: 'Pending', completionDate: '', kycStatus: 'Pending', files: [] });
    } catch (err) {
      alert('Create failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUpdateCustomer = async () => {
    if (!editingCustomer.name?.trim() || !editingCustomer.mobile?.trim()) return alert('Name & Mobile required');
    if (!/^[6-9]\d{9}$/.test(editingCustomer.mobile)) return alert('Valid 10-digit mobile required');

    try {
      const payload = {
        name: editingCustomer.name,
        mobile: editingCustomer.mobile,
        address: editingCustomer.address,
        inspection: editingCustomer.inspection,
        completionDate: editingCustomer.inspection === 'Completed' ? editingCustomer.completionDate : undefined,
        kycStatus: editingCustomer.kycStatus,
      };
      const res = await axiosInstance.put(`/api/customers/${editingCustomer._id}`, payload);
      const newFiles = editingCustomer.files.filter(f => f instanceof File);
      if (newFiles.length) await uploadFiles(res.data._id, newFiles);
      await fetchCustomers();
      setShowEditModal(false);
    } catch {
      alert('Update failed');
    }
  };

  const handleDeleteCustomer = async (id) => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await axiosInstance.delete(`/api/customers/${id}`);
      await fetchCustomers();
    } catch {
      alert('Delete failed');
    }
  };

  /* ---------- CSV IMPORT ---------- */
  const handleSimpleCsvChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith('.csv')) setImportCsvFile(file);
    else {
      alert('Please select a valid CSV file');
      e.target.value = '';
    }
  };


const processSimpleImport = async () => {
  if (!importCsvFile || !selectedImportType) {
    alert('Please select a CSV file and Import Type');
    return;
  }

  const fd = new FormData();
  fd.append('csv', importCsvFile);
  fd.append('uploaderEmail', email);
  fd.append('importType', selectedImportType);  // CRITICAL: Send importType

  try {
    setLoading(true);
    const res = await axiosInstance.post('/api/customers/import-csv', fd);
    
    alert(`Success: ${res.data.created} customers imported\nErrors: ${res.data.totalErrors || 0}`);
    if (res.data.errors?.length) {
      console.warn('Import Errors:', res.data.errors);
    }
    await fetchCustomers();
    setShowImportModal(false);
    setImportCsvFile(null);
    setSelectedImportType('');
  } catch (err) {
    const msg = err.response?.data?.error || err.message;
    alert('Import failed: ' + msg);
  } finally {
    setLoading(false);
  }
};

  const openEditModal = (c) => {
    setEditingCustomer({
      ...c,
      completionDate: c.completionDate ? new Date(c.completionDate).toISOString().split('T')[0] : '',
      files: c.files || [],
    });
    setShowEditModal(true);
  };

  /* ---------- RENDER ---------- */
  return (
    <div style={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem', borderBottom: '2px solid #eee', paddingBottom: '1rem' }}>
        <h2 style={styles.title}>Customer Management</h2>
      </div>

      <div style={{ marginBottom: '1.5rem', textAlign: 'right' }}>
        <button style={styles.button} onClick={() => setShowCreateModal(true)}>New Customer</button>
        <button style={{ ...styles.button, marginLeft: '10px' }} onClick={() => setShowImportModal(true)}>Import CSV</button>
      </div>

      {/* ---------- LIST ---------- */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Customer List {loading && '(Loading...)'}</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th>ID</th><th>Name</th><th>Mobile</th><th>Address</th>
                <th>Inspection</th><th>Date</th><th>KYC</th><th>Files</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c, i) => (
                <tr key={c._id} style={styles.tableRow}>
                  <td>{i + 1}</td>
                  <td>{c.name}</td>
                  <td>{c.mobile}</td>
                  <td>{c.address || '-'}</td>
                  <td style={{ color: c.inspection === 'Completed' ? 'green' : 'orange', fontWeight: 'bold' }}>{c.inspection}</td>
                  <td>{c.completionDate ? new Date(c.completionDate).toLocaleDateString('en-IN') : '-'}</td>
                  <td style={{ color: c.kycStatus === 'Completed' ? 'green' : 'orange' }}>{c.kycStatus}</td>
                  <td>
                    {c.files?.length
                      ? c.files.map(f => <FilePreview key={f.fileId} file={f} customerId={c._id} onFileDeleted={fetchCustomers} />)
                      : '—'}
                  </td>
                  <td>
                    <button style={styles.editButton} onClick={() => openEditModal(c)}>Edit</button>
                    <button style={{ ...styles.editButton, backgroundColor: '#e74c3c', marginLeft: '5px' }} onClick={() => handleDeleteCustomer(c._id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---------- IMPORT MODAL ---------- */}
      {showImportModal && (
      <div style={modalStyles.overlay} onClick={() => {
        setShowImportModal(false);
        setSelectedImportType('');
        setImportCsvFile(null);
      }}>
        <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
          <h3 style={modalStyles.title}>Import Customers from CSV</h3>

          {/* Import Type Dropdown - Now Visible */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '600' }}>
              Select Import Type <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              value={selectedImportType}
              onChange={(e) => setSelectedImportType(e.target.value)}
              style={{
                width: '100%',
                padding: '0.6rem',
                borderRadius: '6px',
                border: '1px solid #ccc',
                fontSize: '1rem'
              }}
            >
              <option value="">-- Choose Import Type --</option>
              <option value="Mandatory Inspection">Mandatory Inspection</option>
              <option value="KYC Validations">KYC Validations</option>
              <option value="Sales Improvement">Sales Improvement</option>
            </select>
          </div>

          {/* Sample CSV Link */}
          <a
            href={`data:text/csv;charset=utf-8,${
              selectedImportType === 'Mandatory Inspection'
                ? 'Customer id,Customer Name,Customer Mobile,Customer Address,mandatory Inspection Status,Mandatory Inspection DaKYC Status\n1234,Rahul,9876543210,"Mumbai",Completed,15/11/2025 Completed'
                : selectedImportType === 'KYC Validations'
                ? 'Customer id,Customer Name,Customer Mobile,Customer Address,KYC Status,Remarks\n5678,Priya,8765432109,"Delhi",Pending,"Aadhaar missing"'
                : selectedImportType === 'Sales Improvement'
                ? 'Customer id,Customer Name,Customer Mobile,Customer Address,Sales Stage,Next Follow-up\n9012,Akash,7654321098,"Bangalore",Interested,25/12/2025'
                : ''
            }`}
            download={`sample-${(selectedImportType || 'template').toLowerCase().replace(/\s+/g, '-')}.csv`}
            style={{
              ...styles.button,
              backgroundColor: '#27ae60',
              textDecoration: 'none',
              padding: '0.5rem 1rem',
              fontSize: '0.9rem',
              marginBottom: '1rem',
              display: selectedImportType ? 'inline-block' : 'none',
            }}
          >
            Download Sample CSV ({selectedImportType || 'Select Type'})
          </a>

          <input type="file" accept=".csv" style={styles.input} onChange={handleSimpleCsvChange} />
          {importCsvFile && <p style={{ color: '#27ae60' }}>Selected: <strong>{importCsvFile.name}</strong></p>}

          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.8rem', justifyContent: 'flex-end' }}>
            <button
              style={styles.button}
              onClick={processSimpleImport}
              disabled={loading || !importCsvFile || !selectedImportType}
            >
              {loading ? 'Importing...' : 'Import CSV'}
            </button>
            <button
              style={{ ...styles.button, backgroundColor: '#95a5a6' }}
              onClick={() => {
                setShowImportModal(false);
                setImportCsvFile(null);
                setSelectedImportType('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}

      {/* ---------- CREATE MODAL ---------- */}
      {showCreateModal && (
        <div style={modalStyles.overlay} onClick={() => setShowCreateModal(false)}>
          <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={modalStyles.title}>Add New Customer</h3>
            <form style={styles.form} onSubmit={handleCreateCustomer}>
              <input style={styles.input} placeholder="Name *" value={newCustomer.name} onChange={e => setNewCustomer({ ...newCustomer, name: e.target.value })} required />
              <input style={styles.input} placeholder="Mobile *" value={newCustomer.mobile} onChange={e => setNewCustomer({ ...newCustomer, mobile: e.target.value })} required />
              <input style={styles.input} placeholder="Address" value={newCustomer.address} onChange={e => setNewCustomer({ ...newCustomer, address: e.target.value })} />

              <select style={styles.select} value={newCustomer.inspection} onChange={e => setNewCustomer({ ...newCustomer, inspection: e.target.value })}>
                {INSPECTION_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>

              {newCustomer.inspection === 'Completed' && (
                <input style={styles.input} type="date" value={newCustomer.completionDate} onChange={e => setNewCustomer({ ...newCustomer, completionDate: e.target.value })} required />
              )}

              <select style={styles.select} value={newCustomer.kycStatus} onChange={e => setNewCustomer({ ...newCustomer, kycStatus: e.target.value })}>
                {KYC_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>

              <div>
                <input type="file" multiple style={styles.input} onChange={e => addNewFiles(e, setNewCustomer)} />
                {newCustomer.files.filter(f => f instanceof File).map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                    <span style={{ fontSize: '0.9rem' }}>{f.name}</span>
                    <button type="button" onClick={() => removeNewFile(i, setNewCustomer)} style={{ background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '3px', padding: '2px 6px' }}>X</button>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="submit" style={styles.button}>Create</button>
                <button type="button" style={{ ...styles.button, backgroundColor: '#95a5a6' }} onClick={() => setShowCreateModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------- EDIT MODAL ---------- */}
      {showEditModal && editingCustomer && (
        <div style={modalStyles.overlay} onClick={() => setShowEditModal(false)}>
          <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={modalStyles.title}>Edit Customer</h3>
            <form style={styles.form} onSubmit={e => { e.preventDefault(); handleUpdateCustomer(); }}>
              <input style={styles.input} placeholder="Name *" value={editingCustomer.name} onChange={e => setEditingCustomer({ ...editingCustomer, name: e.target.value })} required />
              <input style={styles.input} placeholder="Mobile *" value={editingCustomer.mobile} onChange={e => setEditingCustomer({ ...editingCustomer, mobile: e.target.value })} required />
              <input style={styles.input} placeholder="Address" value={editingCustomer.address || ''} onChange={e => setEditingCustomer({ ...editingCustomer, address: e.target.value })} />

              <select style={styles.select} value={editingCustomer.inspection} onChange={e => setEditingCustomer({ ...editingCustomer, inspection: e.target.value })}>
                {INSPECTION_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>

              {editingCustomer.inspection === 'Completed' && (
                <input style={styles.input} type="date" value={editingCustomer.completionDate} onChange={e => setEditingCustomer({ ...editingCustomer, completionDate: e.target.value })} required />
              )}

              <select style={styles.select} value={editingCustomer.kycStatus} onChange={e => setEditingCustomer({ ...editingCustomer, kycStatus: e.target.value })}>
                {KYC_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>

              <div>
                <input type="file" multiple style={styles.input} onChange={e => addNewFiles(e, setEditingCustomer)} />
                {editingCustomer.files.filter(f => f instanceof File).map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                    <span style={{ fontSize: '0.9rem' }}>{f.name}</span>
                    <button type="button" onClick={() => removeNewFile(i, setEditingCustomer)} style={{ background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '3px', padding: '2px 6px' }}>X</button>
                  </div>
                ))}
              </div>

              {editingCustomer.files.filter(f => !(f instanceof File)).length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <strong>Existing files:</strong>
                  {editingCustomer.files.filter(f => !(f instanceof File)).map(f => (
                    <FilePreview key={f.fileId} file={f} customerId={editingCustomer._id} onFileDeleted={fetchCustomers} />
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="submit" style={styles.button}>Update</button>
                <button type="button" style={{ ...styles.button, backgroundColor: '#95a5a6' }} onClick={() => setShowEditModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}